import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchJobAPI } from "../../../services/jobService";
import { fetchRankingsAPI } from "../../../services/resumeService";

const ConsoleContext = createContext(null);

export const useConsole = () => {
  const ctx = useContext(ConsoleContext);
  if (!ctx) throw new Error("useConsole must be used inside <ConsoleProvider>");
  return ctx;
};

/**
 * GET /jobs/ is paginated server-side at limit=10, so the rail walks the pages instead of
 * asking for one oversized one — the rail then shows every open job regardless of what the
 * server's page size happens to be. Capped so a pathological account can't spin here.
 */
const JOBS_PAGE_SIZE = 10;
const MAX_JOB_PAGES = 10;

export const jobIdOf = (job) => job?.["Job Id"] ?? job?.job_id ?? job?.id;

/**
 * Counts now ride along on the jobs payload. This replaces a fan-out that fetched a full
 * rankings payload per job just to read its length.
 *
 * Note screening_count / interview_count count candidates who HAVE been screened /
 * interview-selected — not those whose *current* stage is exactly that. Someone screened
 * and then selected counts in both. That's deliberate.
 *
 * `screening` no longer matches the console's Screening tab. The tab lists candidates still
 * awaiting a screening decision (screened AND not shortlisted); this count is every candidate
 * with a ScreeningCall row. The rail only renders `candidates` and `shortlist`, so the two
 * numbers are never shown side by side and cannot visibly disagree — but do NOT start rendering
 * `screening` here expecting it to match the tab badge. It won't. See index.js:counts.
 */
const statsOf = (job) => ({
  candidates: job.candidate_count ?? 0,
  shortlist: job.shortlist_count ?? 0,
  screening: job.screening_count ?? 0,
  interview: job.interview_count ?? 0,
});

export const ConsoleProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState("");
  const [jobStats, setJobStats] = useState({});

  // jobId -> rankings[]. Cached, so returning to a job you've already opened is free.
  const [rankingsByJob, setRankingsByJob] = useState({});
  // jobId -> in-flight promise, so two callers for the same job share one request.
  const inflight = useRef(new Map());

  /**
   * `quiet` refetches without flipping the loading flag — used when only the counts have
   * changed (shortlisting, scheduling). Without it the rail would blank to skeletons every
   * time you shortlisted someone.
   */
  const loadJobs = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setJobsLoading(true);
    try {
      const collected = [];
      for (let page = 1; page <= MAX_JOB_PAGES; page += 1) {
        const response = await fetchJobAPI(page, JOBS_PAGE_SIZE);
        const batch = Array.isArray(response.jobs) ? response.jobs : [];
        collected.push(...batch);

        const totalPages = response.total_pages || 1;
        if (page >= totalPages || batch.length < JOBS_PAGE_SIZE) break;
      }

      setJobs(collected);
      setJobStats(Object.fromEntries(collected.map((j) => [jobIdOf(j), statsOf(j)])));
      setJobsError("");
    } catch (e) {
      setJobs([]);
      setJobsError(e.message || "Failed to load jobs.");
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  /** Fetch (or reuse) a job's rankings. Always hits the network when `force`. */
  const loadRankings = useCallback(
    async (jobId, { force = false } = {}) => {
      if (!jobId) return [];
      if (!force) {
        if (rankingsByJob[jobId]) return rankingsByJob[jobId];
        const pending = inflight.current.get(jobId);
        if (pending) return pending;
      }

      const promise = fetchRankingsAPI(jobId)
        .then((rankings) => {
          setRankingsByJob((prev) => ({ ...prev, [jobId]: rankings }));
          return rankings;
        })
        .finally(() => inflight.current.delete(jobId));

      inflight.current.set(jobId, promise);
      return promise;
    },
    [rankingsByJob]
  );

  /**
   * Rail counts live on the job payload, so anything that changes them (shortlisting,
   * scheduling, uploading) refreshes the jobs list rather than recounting client-side.
   */
  const refreshCounts = useCallback(() => loadJobs({ quiet: true }), [loadJobs]);

  // ⌘K palette. The layout owns the open state and renders the bar (so the top-bar search
  // can open it from any console route); the Console screen supplies the entries.
  const [commandOpen, setCommandOpen] = useState(false);
  const [commands, setCommands] = useState({ candidates: [], actions: [] });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = {
    jobs,
    jobsLoading,
    jobsError,
    reloadJobs: loadJobs,
    jobStats,
    refreshCounts,
    rankingsByJob,
    loadRankings,
    commandOpen,
    setCommandOpen,
    commands,
    setCommands,
  };

  return <ConsoleContext.Provider value={value}>{children}</ConsoleContext.Provider>;
};
